"use client";

import Link from "next/link";
import Image from "next/image";
import { useRef, useState, useEffect, useCallback } from "react";
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
  AnimatePresence,
} from "framer-motion";
import { FadeInUp, StaggerChildren, StaggerItem } from "@/components/AnimatedSection";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

/* ═══════════════════════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════════════════════ */

const categories = [
  { id: "design", label: "Design", icon: "✦" },
  { id: "development", label: "Development", icon: "⟐" },
  { id: "ai", label: "AI & Automation", icon: "◈" },
] as const;

type CategoryId = (typeof categories)[number]["id"];

interface ServiceItem {
  name: string;
  price: string;
  description: string;
}

interface PricingTier {
  id: CategoryId;
  name: string;
  tagline: string;
  startingPrice: string;
  color: string;
  colorLight: string;
  popular: boolean;
  services: ServiceItem[];
  deliverables: string[];
  timeline: string;
  revisions: string;
}

const tiers: PricingTier[] = [
  {
    id: "design",
    name: "Design",
    tagline: "Brand & Visual Design",
    startingPrice: "499",
    color: "#8B5CF6",
    colorLight: "#A78BFA",
    popular: false,
    services: [
      { name: "UI/UX Design", price: "From $499", description: "Mobile & web interfaces in Figma" },
      { name: "Logo & Brand Identity", price: "From $299", description: "Complete brand kits with guidelines" },
      { name: "Presentation Design", price: "From $199", description: "Pitch decks & keynote slides" },
      { name: "Social Media Kit", price: "From $249", description: "Templates for all platforms" },
      { name: "Dashboard & App Design", price: "From $699", description: "Complex data-driven interfaces" },
    ],
    deliverables: ["Source files (Figma)", "Style guide", "Asset exports", "Developer handoff"],
    timeline: "1-3 weeks",
    revisions: "2-3 rounds included",
  },
  {
    id: "development",
    name: "Development",
    tagline: "Web & App Development",
    startingPrice: "999",
    color: "#E11D24",
    colorLight: "#FF4D53",
    popular: true,
    services: [
      { name: "WordPress / CMS Site", price: "From $999", description: "Blog, business, or portfolio sites" },
      { name: "Custom React / Next.js", price: "From $1,999", description: "High-performance web applications" },
      { name: "Webflow / Framer Site", price: "From $799", description: "No-code with CMS you control" },
      { name: "E-commerce Store", price: "From $1,499", description: "Shopify, WooCommerce, or custom" },
      { name: "Mobile App (React Native)", price: "From $2,999", description: "Cross-platform iOS & Android" },
    ],
    deliverables: ["Full source code", "Deployment & hosting", "SEO optimization", "Performance audit"],
    timeline: "2-8 weeks",
    revisions: "Unlimited during development",
  },
  {
    id: "ai",
    name: "AI & Automation",
    tagline: "Chatbots, RAG & AI Solutions",
    startingPrice: "1,499",
    color: "#3B82F6",
    colorLight: "#60A5FA",
    popular: false,
    services: [
      { name: "Custom AI Chatbot", price: "From $1,499", description: "GPT-powered bots for your business" },
      { name: "RAG Pipeline Setup", price: "From $1,999", description: "Knowledge-base search systems" },
      { name: "LLM Fine-Tuning", price: "From $2,499", description: "Custom models for your domain" },
      { name: "AI Workflow Automation", price: "From $999", description: "Automate repetitive business tasks" },
      { name: "WhatsApp / Slack Bots", price: "From $1,299", description: "Bots on your existing platforms" },
    ],
    deliverables: ["Deployed solution", "API documentation", "Training & onboarding", "30-day support"],
    timeline: "2-6 weeks",
    revisions: "Iterative refinement",
  },
];

const bonuses = [
  {
    title: "Free Design Prototype",
    description: "Experience your design in action before development begins.",
    value: "$500",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
      </svg>
    ),
  },
  {
    title: "Developer Handoff",
    description: "We ensure what we design is exactly what gets built.",
    value: "$750",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
  },
  {
    title: "Project Management",
    description: "Stay on track with expert project management included.",
    value: "$750",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605" />
      </svg>
    ),
  },
  {
    title: "$30 Discovery Call",
    description: "30-min paid consultation — credited toward your project.",
    value: "$500",
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="w-6 h-6">
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
      </svg>
    ),
  },
];

const comparisonData = [
  { metric: "Speed", dc: true, inhouse: false, agency: false, freelancer: true, diy: true },
  { metric: "Flexibility", dc: true, inhouse: false, agency: true, freelancer: false, diy: true },
  { metric: "Quality", dc: true, inhouse: true, agency: true, freelancer: false, diy: false },
  { metric: "Scalability", dc: true, inhouse: false, agency: false, freelancer: false, diy: false },
  { metric: "Affordability", dc: true, inhouse: true, agency: false, freelancer: true, diy: true },
];

const faqs = [
  {
    q: "How does your pricing work?",
    a: "We price per project based on scope and complexity. The prices listed are starting points — after a quick discovery call, we'll give you an exact quote with no hidden fees. You'll know the full cost before we start.",
  },
  {
    q: "Do you offer payment plans?",
    a: "Yes. For projects over $1,000 we offer 50/50 splits (50% upfront, 50% on delivery). For larger projects, we can arrange milestone-based payments so you're never paying for work that hasn't been delivered.",
  },
  {
    q: "What if I need multiple services?",
    a: "Bundle and save. If you need design + development (or any combo), we offer package discounts of 10-20% off the combined price. The more you bundle, the more you save.",
  },
  {
    q: "Are revisions included?",
    a: "Absolutely. Design projects include 2-3 revision rounds. Development projects include unlimited revisions during the build phase. We don't ship until you're 100% satisfied.",
  },
  {
    q: "How fast can you deliver?",
    a: "It depends on the project. Simple designs take 1-2 weeks, websites 2-4 weeks, and complex apps 4-8 weeks. Need it faster? We offer rush delivery for time-sensitive projects.",
  },
  {
    q: "What's the discovery call about?",
    a: "It's a 30-minute paid consultation ($30, credited to your project) where we discuss your goals, timeline, and budget. You'll walk away with a clear proposal — even if you don't hire us.",
  },
];

/* ═══════════════════════════════════════════════════════════════
   ANIMATED COUNTER
   ═══════════════════════════════════════════════════════════════ */

function AnimatedPrice({ value, color }: { value: string; color: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const numericValue = parseInt(value.replace(/,/g, ""));
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!isInView) return;
    const duration = 1200;
    const startTime = Date.now();

    const tick = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * numericValue));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [isInView, numericValue]);

  const formatted = display.toLocaleString();

  return (
    <span ref={ref} className="tabular-nums" style={{ color }}>
      ${formatted}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
   3D TILT CARD
   ═══════════════════════════════════════════════════════════════ */

function TiltCard({
  children,
  className = "",
  glowColor = "#E11D24",
}: {
  children: React.ReactNode;
  className?: string;
  glowColor?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), {
    stiffness: 300,
    damping: 30,
  });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), {
    stiffness: 300,
    damping: 30,
  });

  const handleMouse = useCallback(
    (e: React.MouseEvent) => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      x.set((e.clientX - rect.left) / rect.width - 0.5);
      y.set((e.clientY - rect.top) / rect.height - 0.5);
    },
    [x, y]
  );

  const handleLeave = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouse}
      onMouseLeave={handleLeave}
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
        perspective: 1000,
      }}
      className={className}
    >
      {/* Glow that follows mouse */}
      <motion.div
        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{
          background: `radial-gradient(600px circle at ${50}% ${50}%, ${glowColor}15, transparent 40%)`,
        }}
      />
      {children}
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   ROTATING GRADIENT BORDER
   ═══════════════════════════════════════════════════════════════ */

function GradientBorder({
  children,
  color,
  active,
  className = "",
}: {
  children: React.ReactNode;
  color: string;
  active: boolean;
  className?: string;
}) {
  return (
    <div className={`relative rounded-3xl ${className}`}>
      {active && (
        <div
          className="absolute -inset-[1px] rounded-3xl opacity-60"
          style={{
            background: `conic-gradient(from var(--gradient-angle, 0deg), ${color}, transparent 40%, ${color}80, transparent 70%, ${color})`,
            animation: "gradient-angle-rotate 3s linear infinite",
          }}
        />
      )}
      <div className="relative rounded-3xl bg-surface-light h-full">{children}</div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FAQ ACCORDION
   ═══════════════════════════════════════════════════════════════ */

function FAQItem({ q, a, index }: { q: string; a: string; index: number }) {
  const [open, setOpen] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
    >
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left group"
      >
        <div className="flex items-center justify-between py-6 border-b border-card-border group-hover:border-foreground/20 transition-colors">
          <span className="text-base sm:text-lg font-semibold font-ui pr-8 group-hover:text-red-accent transition-colors">
            {q}
          </span>
          <motion.div
            animate={{ rotate: open ? 45 : 0 }}
            transition={{ duration: 0.2 }}
            className="w-8 h-8 rounded-full border border-card-border flex items-center justify-center flex-shrink-0 group-hover:border-red-accent/40 group-hover:bg-red-accent/5 transition-all"
          >
            <svg className="w-4 h-4 text-foreground/50 group-hover:text-red-accent transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </motion.div>
        </div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
            className="overflow-hidden"
          >
            <p className="py-4 text-foreground/50 font-body leading-relaxed text-sm sm:text-base max-w-3xl">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   COMPARISON BAR
   ═══════════════════════════════════════════════════════════════ */

function ComparisonBar({ label, score, index }: { label: string; score: number; index: number }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-40px" });

  return (
    <div ref={ref} className="flex items-center gap-4">
      <span className="text-xs sm:text-sm font-medium text-foreground/50 w-28 text-right flex-shrink-0">
        {label}
      </span>
      <div className="flex-1 h-8 sm:h-10 bg-surface-light rounded-full overflow-hidden border border-card-border relative">
        <motion.div
          initial={{ width: 0 }}
          animate={isInView ? { width: `${score}%` } : {}}
          transition={{ duration: 1, delay: index * 0.15, ease: [0.25, 1, 0.5, 1] }}
          className="h-full rounded-full relative"
          style={{
            background: `linear-gradient(90deg, #E11D24, #FF4D53)`,
          }}
        >
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold text-white">
            {score}%
          </span>
        </motion.div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════════════ */

export default function PricingPage() {
  const [activeCategory, setActiveCategory] = useState<CategoryId>("development");
  const [expandedService, setExpandedService] = useState<string | null>(null);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 0.95]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 80]);

  const activeTier = tiers.find((t) => t.id === activeCategory)!;

  return (
    <>
      <Navbar />

      {/* ─── HERO ─── */}
      <section
        ref={heroRef}
        className="relative pt-28 sm:pt-32 md:pt-40 pb-24 sm:pb-32 md:pb-40 bg-surface overflow-hidden min-h-[85vh] flex items-center"
      >
        {/* Top logo */}
        <motion.div
          className="absolute top-7 left-1/2 -translate-x-1/2 z-10"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link href="/">
            <Image src="/assets/devcubix-logo-dark.png" alt="Dev Cubix" width={280} height={114} className="h-16 w-auto object-contain dark:hidden block" priority />
            <Image src="/assets/devcubix-logo-white.png" alt="Dev Cubix" width={280} height={114} className="h-16 w-auto object-contain hidden dark:block" priority />
          </Link>
        </motion.div>

        {/* Animated background orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute w-[600px] h-[600px] rounded-full blur-[150px] opacity-[0.15]"
            style={{ background: "#E11D24", top: "-10%", left: "-10%" }}
            animate={{ x: [0, 80, 0], y: [0, -60, 0], scale: [1, 1.2, 1] }}
            transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute w-[500px] h-[500px] rounded-full blur-[130px] opacity-[0.1]"
            style={{ background: "#4D65FF", top: "30%", right: "-15%" }}
            animate={{ x: [0, -70, 0], y: [0, 60, 0], scale: [1, 1.15, 1] }}
            transition={{ duration: 17, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute w-[400px] h-[400px] rounded-full blur-[100px] opacity-[0.08]"
            style={{ background: "#8B5CF6", bottom: "0%", left: "40%" }}
            animate={{ x: [0, 50, -40, 0], y: [0, -40, 30, 0] }}
            transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          />

          {/* Grid pattern overlay */}
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          />
        </div>

        {/* Hero content */}
        <motion.div
          className="relative z-10 max-w-[1100px] mx-auto px-6 w-full"
          style={{ opacity: heroOpacity, scale: heroScale, y: heroY }}
        >
          <div className="text-center">
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
            >
              <p className="section-badge mb-8 mx-auto">Transparent Pricing</p>
            </motion.div>

            {/* Heading with staggered word reveal */}
            <motion.h1
              className="text-4xl sm:text-5xl md:text-[72px] lg:text-[88px] font-bold leading-[1.05] font-ui mb-8 tracking-tight"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <motion.span
                className="inline-block"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                Invest in{" "}
              </motion.span>
              <motion.span
                className="inline-block font-serif italic text-purple-light"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
              >
                Quality
              </motion.span>
              <br />
              <motion.span
                className="inline-block"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                Not{" "}
              </motion.span>
              <motion.span
                className="inline-block relative"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.55 }}
              >
                <span className="relative">
                  Guesswork
                  {/* Strikethrough animation */}
                  <motion.span
                    className="absolute left-0 top-1/2 h-[3px] bg-red-accent rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ duration: 0.8, delay: 1.2, ease: [0.25, 1, 0.5, 1] }}
                  />
                </span>
              </motion.span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              className="text-foreground/50 text-lg md:text-xl max-w-2xl mx-auto font-body leading-relaxed mb-12"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
            >
              No hidden fees. No hourly surprises. Just clear, project-based pricing
              so you know exactly what you&apos;re paying for before we start.
            </motion.p>

            {/* Stats row */}
            <motion.div
              className="flex flex-wrap justify-center gap-8 sm:gap-16"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
            >
              {[
                { value: "190+", label: "Projects Delivered" },
                { value: "50+", label: "Happy Clients" },
                { value: "30%", label: "Below Market Rate" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <p className="text-3xl md:text-4xl font-bold text-red-accent font-ui">{stat.value}</p>
                  <p className="text-xs text-foreground/40 mt-1 uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </motion.div>

            {/* Scroll indicator */}
            <motion.div
              className="mt-16 flex flex-col items-center gap-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.5 }}
            >
              <span className="text-xs text-foreground/30 uppercase tracking-widest">Explore Plans</span>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              >
                <svg className="w-5 h-5 text-foreground/20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-surface to-transparent pointer-events-none" />
      </section>

      {/* ─── CATEGORY SELECTOR + PRICING CARDS ─── */}
      <section className="bg-surface py-16 sm:py-20 md:py-28 relative">
        {/* Background glow for active category */}
        <motion.div
          className="absolute top-20 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full blur-[200px] opacity-[0.08] pointer-events-none"
          animate={{ background: activeTier.color }}
          transition={{ duration: 0.5 }}
        />

        <div className="max-w-[1100px] mx-auto px-6 relative z-10">
          {/* Section heading */}
          <FadeInUp className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-[52px] font-bold leading-[1.1] font-ui mb-4">
              Choose Your{" "}
              <span className="font-serif italic text-purple-light">Path</span>
            </h2>
            <p className="text-foreground/40 font-body text-lg max-w-xl mx-auto">
              Three categories. Clear pricing. Pick what fits your project.
            </p>
          </FadeInUp>

          {/* Category pills */}
          <FadeInUp delay={0.1} className="flex justify-center mb-16">
            <div className="inline-flex bg-surface-light border border-card-border rounded-2xl p-1.5 gap-1">
              {categories.map((cat) => {
                const isActive = activeCategory === cat.id;
                const tier = tiers.find((t) => t.id === cat.id)!;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className="relative px-5 sm:px-8 py-3 rounded-xl text-sm font-semibold transition-all duration-300"
                  >
                    {isActive && (
                      <motion.div
                        layoutId="activePill"
                        className="absolute inset-0 rounded-xl"
                        style={{
                          background: `linear-gradient(135deg, ${tier.color}20, ${tier.color}10)`,
                          border: `1px solid ${tier.color}40`,
                        }}
                        transition={{ type: "spring", stiffness: 400, damping: 30 }}
                      />
                    )}
                    <span
                      className="relative z-10 flex items-center gap-2 transition-colors duration-300"
                      style={{ color: isActive ? tier.color : undefined }}
                    >
                      <span className="text-base">{cat.icon}</span>
                      {cat.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </FadeInUp>

          {/* Pricing cards - 3 side by side */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {tiers.map((tier, i) => {
              const isActive = tier.id === activeCategory;
              return (
                <motion.div
                  key={tier.id}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className={`group relative ${isActive ? "md:-mt-4 md:mb-4" : ""}`}
                  onClick={() => setActiveCategory(tier.id)}
                >
                  <GradientBorder color={tier.color} active={isActive}>
                    <div
                      className={`relative p-6 sm:p-8 rounded-3xl h-full flex flex-col transition-all duration-500 cursor-pointer ${
                        isActive ? "shadow-2xl" : "hover:shadow-lg"
                      }`}
                    >
                      {/* Popular badge */}
                      {tier.popular && (
                        <div
                          className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-bold text-white"
                          style={{ background: tier.color }}
                        >
                          Most Popular
                        </div>
                      )}

                      {/* Hover glow */}
                      <div
                        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                        style={{
                          background: `radial-gradient(ellipse at 50% 0%, ${tier.color}10 0%, transparent 70%)`,
                        }}
                      />

                      {/* Category icon + name */}
                      <div className="flex items-center gap-2 mb-6 relative z-10">
                        <div
                          className="w-10 h-10 rounded-xl flex items-center justify-center text-lg"
                          style={{
                            background: `${tier.color}15`,
                            color: tier.color,
                          }}
                        >
                          {categories.find((c) => c.id === tier.id)?.icon}
                        </div>
                        <div>
                          <h3 className="font-bold font-ui text-lg">{tier.name}</h3>
                          <p className="text-xs text-foreground/40">{tier.tagline}</p>
                        </div>
                      </div>

                      {/* Price */}
                      <div className="mb-6 relative z-10">
                        <div className="flex items-baseline gap-1">
                          <span className="text-xs text-foreground/40 font-medium">From</span>
                        </div>
                        <p className="text-4xl sm:text-5xl font-bold font-ui mt-1">
                          <AnimatedPrice value={tier.startingPrice} color={tier.color} />
                        </p>
                        <p className="text-xs text-foreground/30 mt-1">per project</p>
                      </div>

                      {/* Divider */}
                      <div
                        className="h-px mb-6 opacity-20"
                        style={{ background: `linear-gradient(to right, transparent, ${tier.color}, transparent)` }}
                      />

                      {/* Services list */}
                      <ul className="space-y-3 mb-8 flex-1 relative z-10">
                        {tier.services.map((service, j) => (
                          <li key={j} className="flex items-start gap-3 text-sm">
                            <div
                              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                              style={{ background: `${tier.color}15` }}
                            >
                              <svg
                                className="w-3 h-3"
                                style={{ color: tier.color }}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <span className="text-foreground/80 font-medium">{service.name}</span>
                              <span className="text-foreground/30 ml-1.5 text-xs">{service.price}</span>
                            </div>
                          </li>
                        ))}
                      </ul>

                      {/* Meta info */}
                      <div className="flex items-center gap-4 mb-6 relative z-10">
                        <div className="flex items-center gap-1.5 text-xs text-foreground/40">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {tier.timeline}
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-foreground/40">
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          {tier.revisions}
                        </div>
                      </div>

                      {/* CTA */}
                      <Link
                        href="/contact"
                        className="relative z-10 inline-flex items-center justify-center gap-2 py-3.5 px-6 rounded-full font-semibold text-sm transition-all duration-300 group/btn"
                        style={{
                          background: isActive ? tier.color : `${tier.color}10`,
                          color: isActive ? "#fff" : tier.color,
                          border: isActive ? "none" : `1px solid ${tier.color}30`,
                        }}
                      >
                        <span>Get Started</span>
                        <svg className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                        </svg>
                      </Link>
                    </div>
                  </GradientBorder>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── DETAILED SERVICE BREAKDOWN ─── */}
      <section className="bg-surface py-16 sm:py-20 md:py-28 border-t border-card-border">
        <div className="max-w-[1000px] mx-auto px-6">
          <FadeInUp className="text-center mb-14">
            <p className="section-badge mb-4 mx-auto">Detailed Breakdown</p>
            <h2 className="text-2xl sm:text-3xl md:text-[48px] font-bold leading-[1.1] font-ui">
              What&apos;s{" "}
              <span className="font-serif italic text-purple-light">Inside</span>{" "}
              Each Plan
            </h2>
          </FadeInUp>

          {/* Expandable service rows */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="space-y-3"
            >
              {activeTier.services.map((service, i) => {
                const isExpanded = expandedService === `${activeCategory}-${i}`;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="bg-surface-light border border-card-border rounded-2xl overflow-hidden hover:border-foreground/10 transition-all duration-300"
                  >
                    <button
                      onClick={() =>
                        setExpandedService(
                          isExpanded ? null : `${activeCategory}-${i}`
                        )
                      }
                      className="w-full p-5 sm:p-6 flex items-center justify-between gap-4 text-left"
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <span
                          className="text-2xl font-bold opacity-20 font-ui w-10 flex-shrink-0"
                          style={{ color: activeTier.color }}
                        >
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold font-ui text-base sm:text-lg">
                            {service.name}
                          </h4>
                          <p className="text-xs sm:text-sm text-foreground/40 mt-0.5">
                            {service.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <span
                          className="text-sm font-bold hidden sm:block"
                          style={{ color: activeTier.color }}
                        >
                          {service.price}
                        </span>
                        <motion.div
                          animate={{ rotate: isExpanded ? 180 : 0 }}
                          transition={{ duration: 0.2 }}
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ background: `${activeTier.color}10` }}
                        >
                          <svg
                            className="w-4 h-4"
                            style={{ color: activeTier.color }}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </motion.div>
                      </div>
                    </button>
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="px-5 sm:px-6 pb-6 pt-0">
                            <div
                              className="h-px mb-5 opacity-10"
                              style={{ background: activeTier.color }}
                            />
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div>
                                <h5 className="text-xs font-bold uppercase tracking-wider text-foreground/30 mb-3">
                                  Deliverables
                                </h5>
                                <ul className="space-y-2">
                                  {activeTier.deliverables.map((d, j) => (
                                    <li key={j} className="flex items-center gap-2 text-sm text-foreground/60">
                                      <div
                                        className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                        style={{ background: activeTier.color }}
                                      />
                                      {d}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h5 className="text-xs font-bold uppercase tracking-wider text-foreground/30 mb-3">
                                  Timeline & Revisions
                                </h5>
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2 text-sm text-foreground/60">
                                    <svg className="w-4 h-4 flex-shrink-0" style={{ color: activeTier.color }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Delivery: {activeTier.timeline}
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-foreground/60">
                                    <svg className="w-4 h-4 flex-shrink-0" style={{ color: activeTier.color }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    {activeTier.revisions}
                                  </div>
                                  <div className="flex items-center gap-2 text-sm text-foreground/60">
                                    <svg className="w-4 h-4 flex-shrink-0" style={{ color: activeTier.color }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    100% satisfaction guaranteed
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="mt-5">
                              <Link
                                href="/contact"
                                className="inline-flex items-center gap-2 text-sm font-semibold transition-colors hover:opacity-80"
                                style={{ color: activeTier.color }}
                              >
                                Get a custom quote for {service.name}
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                </svg>
                              </Link>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ─── COMPARISON SECTION ─── */}
      <section className="bg-surface py-16 sm:py-20 md:py-28 relative overflow-hidden">
        {/* Subtle background pattern */}
        <div
          className="absolute inset-0 opacity-[0.02] pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, currentColor 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
          }}
        />

        <div className="max-w-[900px] mx-auto px-6 relative z-10">
          <FadeInUp className="text-center mb-14">
            <p className="section-badge mb-4 mx-auto">How We Compare</p>
            <h2 className="text-2xl sm:text-3xl md:text-[48px] font-bold leading-[1.1] font-ui">
              <span className="font-serif italic text-purple-light">Dev Cubix</span>
              {" "}vs The Rest
            </h2>
          </FadeInUp>

          {/* Visual comparison bars */}
          <div className="space-y-10">
            {/* DC scores */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-8 h-8 rounded-full bg-red-accent flex items-center justify-center text-white text-xs font-bold">
                  DC
                </div>
                <span className="font-bold font-ui text-badge-text">Dev Cubix</span>
              </div>
              <div className="space-y-3">
                <ComparisonBar label="Speed" score={95} index={0} />
                <ComparisonBar label="Flexibility" score={92} index={1} />
                <ComparisonBar label="Quality" score={97} index={2} />
                <ComparisonBar label="Scalability" score={90} index={3} />
                <ComparisonBar label="Affordability" score={94} index={4} />
              </div>
            </div>

            {/* Others comparison grid */}
            <FadeInUp>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-10">
                {[
                  { name: "In-House Team", scores: [48, 22, 68, 15, 12] },
                  { name: "Creative Agency", scores: [20, 38, 80, 42, 8] },
                  { name: "Freelancers", scores: [62, 74, 36, 12, 68] },
                  { name: "DIY Tools", scores: [85, 28, 15, 8, 90] },
                ].map((comp) => (
                  <div
                    key={comp.name}
                    className="bg-surface-light border border-card-border rounded-2xl p-4 text-center"
                  >
                    <p className="text-xs font-bold font-ui mb-3 text-foreground/50">
                      {comp.name}
                    </p>
                    <p className="text-2xl font-bold text-foreground/20">
                      {Math.round(comp.scores.reduce((a, b) => a + b, 0) / comp.scores.length)}%
                    </p>
                    <p className="text-[10px] text-foreground/30 mt-1">avg score</p>
                  </div>
                ))}
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* ─── BONUSES ─── */}
      <section className="bg-surface py-16 sm:py-20 md:py-28 border-t border-card-border relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[200px] bg-red-accent/5 pointer-events-none" />

        <div className="max-w-[1100px] mx-auto px-6 relative z-10">
          <FadeInUp className="text-center mb-14">
            <p className="section-badge mb-4 mx-auto">Included Free</p>
            <h2 className="text-2xl sm:text-3xl md:text-[48px] font-bold leading-[1.1] font-ui">
              <span className="font-serif italic text-purple-light">$2,500</span>{" "}
              in Bonuses
            </h2>
            <p className="text-foreground/40 font-body text-lg mt-4 max-w-lg mx-auto">
              Every project comes with these extras at no additional cost.
            </p>
          </FadeInUp>

          <StaggerChildren className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" staggerDelay={0.1}>
            {bonuses.map((bonus, i) => (
              <StaggerItem key={i}>
                <div className="group relative bg-surface-light border border-card-border rounded-2xl p-6 hover:border-red-accent/20 transition-all duration-500 h-full">
                  {/* Hover gradient */}
                  <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-red-accent/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />

                  <div className="relative z-10">
                    {/* Value badge */}
                    <div className="flex items-center justify-between mb-5">
                      <div
                        className="w-12 h-12 rounded-xl bg-red-accent/10 flex items-center justify-center text-red-accent group-hover:bg-red-accent group-hover:text-white transition-all duration-300"
                      >
                        {bonus.icon}
                      </div>
                      <span className="text-xs font-bold text-foreground/20 group-hover:text-red-accent/40 transition-colors">
                        Worth {bonus.value}
                      </span>
                    </div>

                    <h3 className="font-bold font-ui mb-2 group-hover:text-red-accent transition-colors duration-300">
                      {bonus.title}
                    </h3>
                    <p className="text-sm text-foreground/40 font-body leading-relaxed">
                      {bonus.description}
                    </p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerChildren>
        </div>
      </section>

      {/* ─── TRUST SECTION ─── */}
      <section className="bg-surface py-16 sm:py-20 md:py-28">
        <div className="max-w-[900px] mx-auto px-6">
          <FadeInUp>
            <div className="relative bg-surface-light border border-card-border rounded-3xl p-8 sm:p-12 md:p-16 text-center overflow-hidden">
              {/* Corner accents */}
              <div className="absolute top-0 left-0 w-20 h-20 border-t-2 border-l-2 border-red-accent/20 rounded-tl-3xl" />
              <div className="absolute bottom-0 right-0 w-20 h-20 border-b-2 border-r-2 border-red-accent/20 rounded-br-3xl" />

              {/* Animated ring */}
              <motion.div
                className="w-20 h-20 mx-auto mb-8 rounded-full border-2 border-red-accent/30 flex items-center justify-center"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              >
                <div className="w-14 h-14 rounded-full bg-red-accent/10 flex items-center justify-center">
                  <svg className="w-7 h-7 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                  </svg>
                </div>
              </motion.div>

              <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold font-ui mb-4">
                100% Satisfaction{" "}
                <span className="font-serif italic text-purple-light">Guaranteed</span>
              </h3>
              <p className="text-foreground/50 font-body max-w-lg mx-auto leading-relaxed mb-8">
                We don&apos;t ship until you&apos;re happy. If the final deliverable doesn&apos;t match
                what was agreed upon, we&apos;ll revise it at no extra cost. Your investment is protected.
              </p>

              <div className="flex flex-wrap justify-center gap-8">
                {[
                  { label: "No Hidden Fees", icon: "M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" },
                  { label: "Fixed Pricing", icon: "M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" },
                  { label: "Free Revisions", icon: "M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99" },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2 text-sm text-foreground/50">
                    <svg className="w-5 h-5 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                    </svg>
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section className="bg-surface py-16 sm:py-20 md:py-28 border-t border-card-border">
        <div className="max-w-[800px] mx-auto px-6">
          <FadeInUp className="text-center mb-12">
            <p className="section-badge mb-4 mx-auto">Questions?</p>
            <h2 className="text-2xl sm:text-3xl md:text-[48px] font-bold leading-[1.1] font-ui">
              Pricing{" "}
              <span className="font-serif italic text-purple-light">FAQ</span>
            </h2>
          </FadeInUp>

          <div>
            {faqs.map((faq, i) => (
              <FAQItem key={i} q={faq.q} a={faq.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="py-24 md:py-32 bg-surface text-center relative overflow-hidden">
        {/* Background orbs */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-red-accent/5 blur-[200px]" />
        </div>

        <FadeInUp className="relative z-10 max-w-2xl mx-auto px-6">
          <p className="section-badge mb-6 mx-auto">Ready to get started?</p>

          <h2 className="text-2xl sm:text-4xl md:text-[56px] font-bold font-ui leading-[1.08] mb-5">
            Let&apos;s Build{" "}
            <span className="font-serif italic text-purple-light">Something</span>
            <br />
            Amazing Together
          </h2>

          <p className="text-foreground/50 mb-10 font-body text-lg leading-relaxed">
            Book a discovery call and get a custom quote within 24 hours.
            Your $30 consultation fee is credited toward your project.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <span className="relative inline-block rounded-full p-[2px] gradient-btn-bg">
              <Link
                href="/contact"
                className="relative z-10 inline-flex items-center gap-2 px-8 py-4 rounded-full text-white font-semibold text-sm hover:opacity-90 transition-opacity"
                style={{ background: "linear-gradient(rgb(180, 20, 28) 25%, rgb(225, 29, 36))" }}
              >
                Book a Discovery Call
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </span>

            <Link
              href="https://wa.me/+923190708852"
              className="text-sm font-medium text-foreground/40 hover:text-foreground/70 transition-colors inline-flex items-center gap-2"
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                <path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.492a.75.75 0 00.917.918l4.462-1.494A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-2.386 0-4.592-.826-6.326-2.208l-.442-.36-3.089 1.034 1.034-3.084-.372-.454A9.954 9.954 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
              </svg>
              Chat on WhatsApp
            </Link>
          </div>
        </FadeInUp>
      </section>

      <Footer />
    </>
  );
}
